"use client";

import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Link } from "@tiptap/extension-link";
import { cn } from "@/utils";

interface TiptapSimpleContentProps {
  content: string | any;
  className?: string;
}

export default function TiptapSimpleContent({
  content,
  className = "",
  ...props
}: TiptapSimpleContentProps) {
  // Send postMessage to Tina admin on click
  const handleClick = () => {
    if (typeof window !== "undefined") {
      window.parent.postMessage({ type: "tiptap-simple-click" }, "*");
    }
  };
  // Parse content
  let jsonContent = null;
  let isPlainText = false;

  if (content && typeof content === "string") {
    const trimmed = content.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        jsonContent = JSON.parse(content);
      } catch {
        isPlainText = true;
      }
    } else {
      isPlainText = true;
    }
  } else if (content && typeof content === "object") {
    jsonContent = content;
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bold: false,
        italic: false,
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        link: false,
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: "tiptap-simple-link",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ],
    content: jsonContent,
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: cn("tiptap-simple-content", className),
      },
    },
  });

  useEffect(() => {
    if (editor && jsonContent) {
      queueMicrotask(() => {
        if (editor && !editor.isDestroyed) {
          const current = editor.getJSON();
          if (JSON.stringify(current) !== JSON.stringify(jsonContent)) {
            editor.commands.setContent(jsonContent);
          }
        }
      });
    }
  }, [content, editor, jsonContent]);

  // Plain text fallback (legacy content)
  if (isPlainText) {
    return (
      <span
        className={cn("tiptap-simple-content whitespace-pre-wrap", className)}
        onClick={handleClick}
        {...props}
      >
        {content}
      </span>
    );
  }

  if (!jsonContent || !editor) return null;

  return (
    <div
      onClick={handleClick}
      onClickCapture={(e) => {
        setTimeout(() => {
          window.parent.postMessage({ type: "tiptap-simple-click" }, "*");
        }, 500);
      }}
      {...props}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
